/**
 * HR Suite surface tokens, matched to the approved Figma frames.
 *
 * These exist because the frames do NOT use the CRM's card recipe. Three
 * concrete differences, each of which read as "not matching" side by side:
 *
 *  1. The border is a hairline. `--color-black-3` (#b4bab8) is a mid grey and
 *     draws a visible box around every widget; the frames use a barely-there
 *     line and let a soft shadow do the separating.
 *  2. Cards are not flat white. They carry a faint warm-white gradient so they
 *     lift off `--color-background` (#e7ebea) instead of sitting flush on it.
 *  3. Row separators inside a card are lighter still — at #b4bab8 a six-row
 *     list reads as a table with rules, which is not the intent.
 */
export const HR_SURFACE = {
  border: "#E4EAE7",
  /**
   * The widget fill is a green WASH that NEVER reaches pure white.
   *
   * This is the correction that mattered: an earlier version ran the gradient
   * out to #FFFFFF, so most of every card was flat white — the tint only showed
   * in one corner, and the white row sheets inside became invisible against it.
   * In the frames the card stays tinted corner to corner and the rows read as
   * white bars floating on it. Keep the lightest stop off white.
   */
  bg: "#FFFFFF",
  shadow: "0 1px 2px rgba(20, 61, 45, 0.04), 0 18px 40px -24px rgba(20, 61, 45, 0.22)",
  divider: "#EDF1EF",
  radius: "24px",
  /**
   * The HR body. Deliberately NOT the CRM's `--color-background` (#e7ebea):
   * once the cards are green, the page behind them has to go the other way —
   * a flatter, more neutral grey — or cards and body sit at the same tint and
   * the whole screen reads as one wash.
   *
   * SOLID, not a gradient. `HrPageHeader` paints this same value on its sticky
   * bar so scrolled content is hidden behind it; a gradient would resolve over
   * that bar's own ~120px height and could never line up with the same gradient
   * spanning the full scroll height behind it, leaving a visible seam.
   */
  page: "#EAEDEB",
} as const;

/**
 * List rows do NOT sit directly on the wash — the frames float them on a white
 * sheet inside the card, which is what makes the Leave Requests / Recent
 * activities / Employee status rows read as white bars against a tinted widget.
 */
export const HR_ROW_SHEET = {
  bg: "#FFFFFF",
  shadow: "0 1px 2px rgba(30, 48, 42, 0.05)",
  radius: "14px",
} as const;

/**
 * The tinted chip behind a leading icon.
 *
 * UNIFORM MINT, every tile. The frames deliberately do not colour-code the
 * icon — an "Absent today" tile has the same green chip as "Present today",
 * and the semantic colour lives in the pill beside the number instead. Tinting
 * both double-encodes the meaning and makes the row read as an alert strip.
 */
export const HR_ICON_CHIP = { bg: "#E7F1EB", fg: "#218760" } as const;

/**
 * Text colours, defined here because THE GLOBAL TOKENS DO NOT EXIST.
 *
 * `--color-text-primary`, `--color-text-secondary` and `--color-text-gray` are
 * referenced 228 times across this codebase and are declared in NEITHER
 * `styles/variables.css` NOR any `@theme` block. `var()` on an undefined custom
 * property makes the whole declaration invalid, so:
 *
 *   - `color: var(--color-text-primary)`  → dropped → the element just inherits.
 *     Text still looks darkish, which is why this went unnoticed for so long.
 *   - `background: var(--color-text-primary)` → dropped → TRANSPARENT.
 *
 * That second case is why the active sub-tab underline never appeared, and the
 * first is why every "grey" sub-label rendered at full text colour instead.
 *
 * Applied as real custom properties on HR's root container (see `hrRoutes`), so
 * the ~200 HR call sites resolve correctly without changing the CRM, which has
 * been living with the same broken tokens and would shift visually if they were
 * defined globally. Fixing it at `:root` is the proper repair — that is the
 * user's call, not a side effect of an HR ticket.
 */
export const HR_TEXT = {
  primary: "#051912",
  secondary: "#374741",
  gray: "#697571",
} as const;

/**
 * The LIST PANEL: one white sheet holding a toolbar, a sticky column header
 * and a body that scrolls on its own, so the page around it (title, tabs,
 * KPI strip) never scrolls away. Rows are separated by hairlines, not floated
 * as individual cards: at 50+ people per day a stack of bordered cards cost a
 * screen per dozen people.
 */
export const HR_PANEL = {
  bg: "#FFFFFF",
  border: "#E4EAE7",
  radius: "24px",
  shadow: "0 1px 2px rgba(20, 61, 45, 0.04), 0 18px 40px -24px rgba(20, 61, 45, 0.22)",
  /** Column header and sticky group headers. */
  head: "#F7FAF8",
  divider: "#EEF2F0",
  hover: "#F5FAF7",
} as const;

/**
 * The KPI strip's chrome — the Reports card recipe (`Dashboard/v2/ui/Card`):
 * mint-to-white wash, #d6ebe3 hairline, soft drop. One strip with dividers
 * between the numbers, instead of a row of separate tiles.
 */
export const HR_STRIP = {
  bg: "linear-gradient(161deg, rgba(233,252,245,0.80) 11.47%, rgba(255,255,255,0.72) 52.35%)",
  border: "#D6EBE3",
  shadow: "0 6px 10px rgba(30, 48, 42, 0.03)",
  divider: "#E3EEE9",
} as const;

/**
 * Viewport-fit layout for list screens (lg and up): the page root fills HR's
 * scroll frame and a panel takes the height that is left, scrolling inside.
 * `min-h` is the escape hatch for short windows — below it the frame scrolls
 * the whole page again rather than squeezing the list to a sliver. Below lg
 * nothing is locked: the page scrolls and each panel caps itself.
 */
export const HR_FIT_PAGE = "flex flex-col p-6 pb-5 lg:flex-1 lg:min-h-[640px]";
/** A panel that takes the rest of a fit page (lg+); below lg, capped and scrolling. */
export const HR_FILL = "max-h-[78vh] lg:max-h-none lg:flex-1 lg:min-h-0";

/**
 * Page-header buttons at 36px, to sit with the compact title. `!h-9`
 * because `secondary-btn` / `primary-btn` fix their height in unlayered
 * global CSS, which beats a plain Tailwind utility.
 */
export const HR_HEADER_BTN = "!h-10 !rounded-full gap-2 px-4 text-[14px]";

/** Sub-tab strip — active is near-black with a matching underline. */
export const HR_TAB = {
  active: HR_TEXT.primary,
  inactive: HR_TEXT.gray,
  underline: HR_TEXT.primary,
} as const;

export type HrPillTone = "good" | "warn" | "bad" | "muted";

/** Pill palette. `warn` is the amber the WFH share uses in the frames. */
export const HR_PILL_TONE: Record<HrPillTone, { bg: string; fg: string }> = {
  good: { bg: "#E8F5EE", fg: "#17714F" },
  warn: { bg: "#FFF4E0", fg: "#8A5A00" },
  bad: { bg: "#FDECEC", fg: "#B42D2C" },
  muted: { bg: "#F1F4F2", fg: "#6B7A74" },
};
